import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import classNames from 'classnames';
import { useId } from 'react';
import { FieldFooter } from '../../atoms/field-footer';
import { ChevronDownXs } from '../../icons/arrows/chevron-down-xs';
import { ExclamationMarkTriangleXs } from '../../icons/symbols/exclamation-mark-triangle-xs';
import styles from './select.module.css';
const Option = ({ ...props })=>/*#__PURE__*/ _jsx("option", {
        ...props
    });
/**
 * `Select` is a customizable select component with integrated labeling, optional footer, and error message support.
 *
 * @property {ReactElement<typeof Option>[]} [children] - The option elements to be rendered inside the select.
 * @property {string} label - The text label describing the select field.
 * @property {string} [className] - Additional CSS classes for the wrapper element.
 * @property {string} [emptyOptionLabel] - Label shown as the only option to prompt the user. When provided, the select appears visually disabled, blocking user interaction, but still allows native form validation to work.
 * @property {string} [errorMessage] - An optional error message displayed below the select input.
 * @property {ReactNode} [footer] - An optional footer for additional help text.
 *
 * @example
 * ```tsx
 * <Select
 *   label="Select your country"
 *   footer="Please choose carefully."
 *   errorMessage="This field is required."
 *   value={selectedCountry}
 *   onChange={handleCountryChange}
 * >
 *   <Select.Option value="de">Germany</Select.Option>
 *   <Select.Option value="us">United States</Select.Option>
 * </Select>
 * ```
 */ export const Select = ({ children, className, 'data-testid': testId, emptyOptionLabel, errorMessage, footer, label, ...props })=>{
    const id = useId();
    const labelId = `${id}-label`;
    const footerId = footer ? `${id}-footer` : undefined;
    return /*#__PURE__*/ _jsxs("div", {
        className: classNames(styles.wrapper, className),
        "data-testid": testId,
        children: [
            /*#__PURE__*/ _jsx("label", {
                className: classNames(styles.label, props.disabled && styles['label-disabled']),
                htmlFor: id,
                id: labelId,
                children: label
            }),
            /*#__PURE__*/ _jsxs("select", {
                className: styles.select,
                id: id,
                ...props,
                "aria-disabled": Boolean(emptyOptionLabel),
                "aria-labelledby": [
                    labelId,
                    footerId
                ].filter(Boolean).join(' '),
                children: [
                    /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        children: [
                            /*#__PURE__*/ _jsx("selectedcontent", {}),
                            /*#__PURE__*/ _jsx(ChevronDownXs, {})
                        ]
                    }),
                    emptyOptionLabel ? /*#__PURE__*/ _jsx(Option, {
                        className: styles['empty-option'],
                        value: "",
                        children: emptyOptionLabel
                    }) : children
                ]
            }),
            footer && /*#__PURE__*/ _jsx(FieldFooter, {
                className: styles.footer,
                "data-testid": "field-footer",
                id: footerId,
                children: footer
            }),
            errorMessage && /*#__PURE__*/ _jsx(FieldFooter, {
                className: styles['error-message'],
                "data-testid": "field-error",
                error: true,
                icon: /*#__PURE__*/ _jsx(ExclamationMarkTriangleXs, {}),
                children: errorMessage
            })
        ]
    });
};
Select.Option = Option;

//# sourceMappingURL=index.js.map